// De woorden uit de database (WidgetTranslate, "languageWords") bijgewerkt via de widget-teksten (teksten.json, 13 talen).
// Widgetaudit bug 6 (Martijn 10-10-2026).
//
// De vertaallijst in de database spreekt de gast in het Nederlands aan met "u" ("Start uw reservering", "Uw gegevens",
// "Wilt u ...") terwijl de hotelwidget en de rest van Jimani "je" zeggen, en hij heeft een paar woorden die niet kloppen:
// "Gast" boven het aantal gasten, "Extra Producten". Die lijst is voor alle winkels samen; ze in de database aanpassen is
// een schrijfactie op live. Daarom hier, op de plek waar de lijst binnenkomt (setLanguageWords in de slices):
//
//  * JE_VORM -- alleen in een Nederlandse widget: de andere talen hebben hun eigen aanspreekvorm (besluit 28-09/30-09:
//    informeel en/da/sv/no/fi/es, formeel de/fr/it/pl), die laten we staan.
//  * IN_ELKE_TAAL -- in elke taal, maar alleen als teksten.json voor die taal een vertaling heeft (anders zou er in een
//    Duitse widget een Nederlands woord komen zolang het bestand nog niet binnen is; dan blijft het woord uit de database).
//
// De teksten staan letterlijk in een aanroep van jmVertaal in de code, zodat tools/vertalingen/codeteksten.cjs ze vindt en vertaalt.
import { huidigeTaal, vertaal as jmVertaal } from './woorden';

/** Sleutel in languageWords -> Nederlandse tekst in de je-vorm. Sleutels zoals de hooks ze maken: zonder {} en spaties. */
export function jeVorm() {
    const gegevens = jmVertaal("Je gegevens");
    const reservering = jmVertaal("Je reservering");
    const eenOptie = jmVertaal("Je kunt maar 1 optie kiezen");
    return {
        widgetHeading: jmVertaal("Start je reservering"),
        yourDetails: gegevens,
        YourDetail: gegevens,
        yourdetails: gegevens,
        yourBooking: reservering,
        YourBooking: reservering,
        ReservationSuccessInfo: jmVertaal("Je reservering is aangemaakt. Je krijgt zo een bevestiging per e-mail."),
        ReservationSuccessMessage: jmVertaal("Je reservering is gelukt"),
        reservationNumberText: jmVertaal("Je reserveringsnummer"),
        PaymentFailInfo: jmVertaal("De betaling is helaas niet gelukt. De reservering is niet geplaatst; je kunt het opnieuw proberen."),
        PaymentPendingInfo: jmVertaal("We kunnen nog niet zien of de betaling is gelukt; dat kan even duren. Zodra je hebt betaald, krijg je bericht. Lukt de betaling niet, dan is de reservering niet geplaatst en kun je opnieuw reserveren."),
        PaymentPendingMessage: jmVertaal("Je betaling wordt verwerkt"),
        CancelReservation: jmVertaal("Annuleer je reservering"),
        CancelWidgetButtonText: jmVertaal("Annuleer je boeking"),
        ReservationCancelSuccessMessage: jmVertaal("Je boeking is geannuleerd"),
        TermsandCondtionsText: jmVertaal("Je moet akkoord gaan met de algemene voorwaarden"),
        CombinationInfo: jmVertaal("We hebben nog leuke extra activiteiten voor je. Voeg ze meteen toe aan je reservering."),
        deleteinfo: jmVertaal("Weet je zeker dat je dit wilt verwijderen?"),
        deletewarning: jmVertaal("Weet je het zeker?"),
        CartDetails: jmVertaal("Je winkelwagen"),
        FooterInfo: jmVertaal("Je kunt veilig je bestelling plaatsen, dan gaan wij voor je aan de slag."),
        BuyTickets: jmVertaal("Koop je tickets"),
        validationOne: eenOptie,
        validationThree: eenOptie,
        validationFive: eenOptie,
        validationSeven: eenOptie,
        AlreadySubmittedSubtitle: jmVertaal("Het lijkt erop dat je de enquête al hebt ingevuld."),
        feedbackHeader: jmVertaal("Een overzicht van je feedback"),
        HeaderSuccess: jmVertaal("Hartelijk dank voor je feedback!"),
        leremipsum: jmVertaal("Help ons beter te worden met een cijfer. Je kunt per vraag een opmerking toevoegen."),
        postFeedback: jmVertaal("Wil je je feedback openbaar delen?"),
        Typehere: jmVertaal("Je kunt ons hier extra feedback sturen."),
        yourDetailLoremIpsum: jmVertaal("Na het invullen van alle vragen zie je hier je gemiddelde oordeel."),
        SuccessPoints: jmVertaal("Voeg je verdiende punten toe aan je account."),
    };
}

/** Sleutel -> Nederlandse tekst, voor elke taal (via teksten.json). */
export function inElkeTaal() {
    const extra = jmVertaal("Extra producten");
    return {
        Guest: jmVertaal("Gasten"),
        ExtraProducts: extra,
        "combination_extraproduct)": extra,
    };
}

// De Nederlandse bron van inElkeTaal, om te zien of er een vertaling is (jmVertaal geeft de bron terug als die er niet is).
const BRON_ELKE_TAAL = { Guest: "Gasten", ExtraProducts: "Extra producten", "combination_extraproduct)": "Extra producten" };

/**
 * De woorden uit de database met de je-vorm (Nederlands) en de verbeterde woorden (elke taal) erover.
 *
 * @param {object} woorden  languageWords zoals de hook hem maakt (sleutel -> tekst)
 * @param {string} [taal]   de taal van de widget; standaard die van i18next
 */
export function metWidgetteksten(woorden, taal = huidigeTaal()) {
    if (!woorden || typeof woorden !== 'object') return woorden;
    const uit = { ...woorden };
    const nl = !taal || String(taal).toLowerCase().startsWith('nl');

    if (nl) Object.assign(uit, jeVorm());

    const elk = inElkeTaal();
    for (const [sleutel, tekst] of Object.entries(elk)) {
        if (nl || tekst !== BRON_ELKE_TAAL[sleutel]) uit[sleutel] = tekst;
    }
    return uit;
}

/**
 * De eigen koptekst uit de opmaak van de widget (MainText_fieldText), of null. De oude standaardtekst "Start uw reservering"
 * staat bij veel widgets als opgeslagen tekst (meegekopieerd uit het sjabloon); dat is geen eigen keuze, dus dan de
 * standaardkop in de je-vorm. (widgetaudit bug 6)
 */
export function eigenKop(tekst) {
    if (typeof tekst !== 'string' || !tekst.trim()) return null;
    return /^start\s+uw\s+reservering\.?$/i.test(tekst.trim()) ? null : tekst;
}
